/// <reference types="vite/client" />
import { z } from 'zod';
import { ENV_TYPES, isGovEnvType } from '@strength/utils';

const envVariablesSchema = z.object({
  VITE_API_URL: z.string(),
  VITE_WS_URL: z.string(),
  VITE_OKTA_DOMAIN: z.string(),
  VITE_OKTA_CLIENT_ID: z.string(),
  VITE_DB_HASH: z.string(),
  VITE_GAINSIGHT_PX_KEY: z.string(),
  VITE_COMMIT_SHA: z.string(),
  VITE_AWS_S3_URL: z.string(),
  VITE_ENVIRONMENT: z.enum([
    'local',
    'staging',
    'staging-govcloud',
    'production',
    'production-govcloud',
    'production-govcloud-il2',
  ]),
  VITE_REGION: z.enum(['us', 'eu', 'ap']),
  VITE_TW_MFE_ENV: z.enum(['latest', 'staging', 'production']).optional(),
  VITE_CDP_API_KEY: z.string(),
  VITE_CDP_SITE_ID: z.string(),
  VITE_ENV_TYPE: z.enum(ENV_TYPES).default('commercial'),
  VITE_DD_SITE: z.string(),
  VITE_DD_CLIENT_TOKEN: z.string(),
  VITE_DD_APPLICATION_ID: z.string(),
  VITE_ZERO_CACHE_URL: z.string(),
}).transform((env) => ({
  ...env,
  VITE_IS_GOV: isGovEnvType(env.VITE_ENV_TYPE)
}));

// When the web is served through portless at `<slug>.web.strength.localhost`
// (multi-worktree local dev), point API/WS at the matching `<slug>.api.strength.localhost`
// so each worktree's web talks to its own server without hard-coded ports,
// and route zero-cache through the web origin (`/zero-cache`, proxied by
// vite.config.ts to the worktree's container) so the `.strength.localhost`
// session cookie reaches zero-cache.
function portlessOverrides(): Partial<z.infer<typeof envVariablesSchema>> {
  if (typeof window === 'undefined') return {};
  const { hostname, port, protocol } = window.location;
  const match = hostname.match(/^(.+)\.web\.strength\.localhost$/);
  if (!match) return {};
  const apiHost = `${match[1]}.api.strength.localhost${port ? `:${port}` : ''}`;
  const wsProto = protocol === 'https:' ? 'wss:' : 'ws:';
  return {
    VITE_API_URL: `${protocol}//${apiHost}`,
    VITE_WS_URL: `${wsProto}//${apiHost}/sync`,
    VITE_ZERO_CACHE_URL: `${protocol}//${hostname}/zero-cache`,
  };
}

// Non-portless local dev: derive the zero-cache URL from the local container's
// host port instead of the committed config default, so each worktree's web
// talks to its own zero-cache. The port is injected by vite.config.ts's define
// from process.env.ZERO_CACHE_HOST_PORT (the worktree root .env, loaded by
// Nx's loadRootEnvFiles); falls back to the compose default when unset
// (e.g. the main checkout, whose zero-cache serves on 4848).
function localZeroCacheOverrides(): Partial<z.infer<typeof envVariablesSchema>> {
  const port = import.meta.env['VITE_ZERO_CACHE_HOST_PORT'] || '4848';
  return { VITE_ZERO_CACHE_URL: `http://localhost:${port}` };
}

// DEV-only overrides, applied last (highest precedence). Production reads
// these values straight from the deployed config files.
function devOverrides(): Partial<z.infer<typeof envVariablesSchema>> {
  const portless = portlessOverrides();
  return Object.keys(portless).length > 0 ? portless : localZeroCacheOverrides();
}

export const environment = envVariablesSchema.parse({
  // Generated variables (from vite.config.ts)
  ...import.meta.env,
  // Environment/region variables
  ...window['APP_CONFIG'],
  ...(import.meta.env.DEV ? devOverrides() : {}),
});
